import { EditorShell, InspectorGroup, useAutosave, type ValidationItem } from '@components/Common/EditorShell';
import ActionIconButton from '@components/Common/ActionIconButton';
import ConfirmDialog from '@components/Common/ConfirmDialog';
import FormField from '@components/Common/FormField';
import SelectFormField from '@components/Common/SelectFormField';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import { useToast } from '@components/Common/Toast/ToastProvider';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import SourceOutlinedIcon from '@mui/icons-material/SourceOutlined';
import { Box, Button } from '@mui/material';
import axios from 'axios';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { checkGuideHtml } from '@shared/guideHtmlRules';

import { type GuideTitle, bodyWarnings } from './guideWarnings';
import HeroImageField from './HeroImageField';
import HtmlBodyEditor from './HtmlBodyEditor';
import ImportReport from './ImportReport';
import { type ImportReportData, formatCost, reportFromDone } from './importReportData';
import StructuredBodyEditor from './StructuredBodyEditor';
import {
  SECTION_LABELS,
  deriveSlug,
  htmlReadingStats,
  missingRequired,
  parseGuide,
  publicPath,
  readingStats,
} from './sections';
import { seedFor, validateStructured } from './structured/fieldMap';
import './editor.scss';
import { useBeforeUnloadWhile } from '../useBeforeUnloadWhile';
import { useSlugAvailability } from '../useSlugAvailability';

const PAGE_TYPE_OPTIONS = [
  { value: 'skill-guide', label: 'Academy guide (skill)' },
  { value: 'hiring-guide', label: 'Hiring guide' },
  { value: 'career-guide', label: 'Career guide' },
];

/* Every status the system writes, not just the three an operator can choose. IMPORTING and
   IMPORT_FAILED used to render as "Draft", which is how a dead import hid in plain sight. */
const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Published',
  ARCHIVED: 'Archived',
  DRAFT: 'Draft',
  IMPORTING: 'Importing',
  IMPORT_FAILED: 'Import failed',
};

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'ACTIVE', label: 'Published' },
  { value: 'ARCHIVED', label: 'Archived' },
];

/** The public card truncates the excerpt here. */
const EXCERPT_LIMIT = 180;

/** A title is one line on the site; Enter in its (wrapping) field must not add a break. */
const blockEnter = (e: React.KeyboardEvent) => {
  if (e.key === 'Enter') e.preventDefault();
};

interface GuideEditorShellProps {
  guideId: string;
}

const GuideEditorShell: React.FC<GuideEditorShellProps> = ({ guideId }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [datePublished, setDatePublished] = useState('');
  const [status, setStatus] = useState('DRAFT');
  const [pageType, setPageType] = useState('hiring-guide');
  const [contentJson, setContentJson] = useState('');
  /**
   * The body as one HTML document, for guides authored that way.
   *
   * Empty means this guide still renders from contentJson and the section editor. Both are kept on
   * the record, so emptying this field here is how a guide goes back to the template.
   */
  const [contentHtml, setContentHtml] = useState('');
  const [sourceFileUrl, setSourceFileUrl] = useState('');
  const [viewCount, setViewCount] = useState<number | null>(null);

  const [dirty, setDirty] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishConfirm, setPublishConfirm] = useState(false);
  const [reimporting, setReimporting] = useState(false);
  // A re-import is the same four to six minutes as an import, and its result lives only here.
  useBeforeUnloadWhile(reimporting);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const reimportInput = useRef<HTMLInputElement | null>(null);
  /** What the last re-import reported (problems, notes, cost). Not stored anywhere else. */
  const [reimportReport, setReimportReport] = useState<ImportReportData | null>(null);

  /** Slug at load — changing it after publish breaks live links. */
  const publishedSlug = useRef('');

  const markDirty = () => setDirty(true);

  useEffect(() => {
    axios
      .get(`/api/guides/${guideId}`)
      .then(({ data }) => {
        setTitle(data.title ?? '');
        setSlug(data.slug ?? '');
        setExcerpt(data.excerpt ?? '');
        setDatePublished(data.datePublished ?? '');
        setStatus(data.status ?? 'DRAFT');
        setPageType(data.pageType ?? 'hiring-guide');
        setContentJson(data.contentJson ?? '');
        setContentHtml(data.contentHtml ?? '');
        setSourceFileUrl(data.sourceFileUrl ?? '');
        setViewCount(typeof data.viewCount === 'number' ? data.viewCount : null);
        publishedSlug.current = data.status === 'ACTIVE' ? (data.slug ?? '') : '';
        setDirty(false);
      })
      .catch(() => showToast('Failed to load guide', 'error'))
      .finally(() => setLoading(false));
  }, [guideId, showToast]);

  /*
   * Published guide titles, for the "listed without links" warning. Fetched once per editor
   * session: the list changes when a guide is published, not while this one is being edited. A
   * failure only means that one warning is not shown, so it is not reported.
   */
  const [guideTitles, setGuideTitles] = useState<GuideTitle[]>([]);
  useEffect(() => {
    let live = true;
    axios
      .get('/api/guides/titles')
      .then(({ data }) => {
        if (live && Array.isArray(data)) setGuideTitles(data);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  /* ── Body ─────────────────────────────────────────────────────────────── */

  const parsed = useMemo(() => parseGuide(contentJson), [contentJson]);
  const hasHtmlBody = contentHtml.trim().length > 0;
  const missing = useMemo(() => missingRequired(parsed.obj, pageType), [parsed.obj, pageType]);
  // An HTML guide is counted from its HTML: the envelope beside it is not what a reader gets.
  const stats = useMemo(
    () => (hasHtmlBody ? htmlReadingStats(contentHtml) : readingStats(parsed.obj)),
    [hasHtmlBody, contentHtml, parsed.obj],
  );

  const editBody = useCallback((update: (prev: string) => string) => {
    setContentJson(update);
    setDirty(true);
  }, []);

  const addMissingSection = useCallback((name: string) => {
    setContentJson((prev) => {
      const p = parseGuide(prev);
      if (!p.ok) return prev;
      // Seeded with the section's own shape from the field map (an object with a heading, a list),
      // so the typed editor opens on it rather than the JSON fallback for an empty string.
      return JSON.stringify({ ...p.obj, [name]: seedFor(pageType, name) }, null, 2);
    });
    setDirty(true);
  }, [pageType]);

  /** Inline problems in the typed fields, summarised into the validation bar. */
  const fieldIssues = useMemo(
    () => (hasHtmlBody || !parsed.ok ? [] : validateStructured(parsed.obj, pageType)),
    [hasHtmlBody, parsed, pageType],
  );

  /* ── Validation ───────────────────────────────────────────────────────── */

  /*
   * Asked of the server, excluding this guide. It was a list of every other guide's slug, read
   * from a list route capped at 500, so a collision past that point passed this check and was
   * found by the save.
   */
  const slugCheck = useSlugAvailability(slug, guideId);

  const slugChangedAfterPublish =
    !!publishedSlug.current && slug.trim() !== publishedSlug.current;

  const validation = useMemo<ValidationItem[]>(() => {
    const items: ValidationItem[] = [];

    if (!parsed.ok) {
      items.push({ id: 'json', level: 'blocker', message: `Guide body is not valid JSON: ${parsed.error}` });
      return items;
    }
    // All three appear on the public cards, so an empty one ships a broken card.
    if (!title.trim()) items.push({ id: 'title', level: 'blocker', message: 'Title is empty, and it appears on the public card.' });
    if (!slug.trim()) items.push({ id: 'slug', level: 'blocker', message: 'Slug is empty, and it is the public URL.' });
    else if (slugCheck.taken) {
      items.push({ id: 'slug-dupe', level: 'blocker', message: `Slug "${slug.trim()}" is already used by another guide.` });
    }
    if (!excerpt.trim()) items.push({ id: 'excerpt', level: 'blocker', message: 'Excerpt is empty, and it appears on the public card.' });
    /*
     * The date is part of the envelope, not the body, so the section check never asked for it and an
     * HTML-authored guide skips that check anyway. The page prints it and the JSON-LD gives it to
     * Google, so an empty one ships a guide dated nothing. The server refuses this on publish now,
     * and the two have to agree or the editor says one thing and the save says another.
     */
    // `ParsedGuide.obj` is `{}`, since a guide body is whatever the JSON says; read through a
    // narrow shape rather than widening the parser's type for one field.
    const seoDate = (parsed.obj as { seo?: { datePublished?: string } })?.seo
      ?.datePublished;
    if (!datePublished.trim() && !seoDate) {
      items.push({
        id: 'date',
        level: 'blocker',
        message: 'Date published is empty. The page shows it and Google reads it.',
      });
    }

    /*
     * Which body check applies follows from how the guide is authored. An HTML guide has no named
     * sections to be missing, so the section check would block every one of them; a template guide
     * has no HTML to hold to the house rules. The server makes the same choice on save, and the two
     * have to agree or the editor says one thing and the save says another.
     */
    if (hasHtmlBody) {
      const problems = checkGuideHtml(contentHtml, title);
      if (problems.length) {
        items.push({
          id: 'html-rules',
          level: 'blocker',
          message: `The HTML body breaks ${problems.length} house rule(s). ${problems[0]}`,
        });
      }
    } else if (missing.length) {
      items.push({
        id: 'missing-sections',
        level: 'blocker',
        message: `The live page reads ${missing.map((m) => SECTION_LABELS[m] ?? m).join(', ')} without a null guard, so publishing without ${missing.length === 1 ? 'it' : 'them'} renders an error boundary.`,
        fixLabel: 'Add empty sections',
        onFix: () => missing.forEach(addMissingSection),
      });
    }

    /*
     * The typed editors show each problem beside its field; the bar says how many there are and
     * where the first one is, because the field may be thirty sections down. Errors are the ones
     * the page would get wrong (an image with no description, a bar with no number); everything
     * else is a warning.
     */
    const fieldErrors = fieldIssues.filter((i) => i.level === 'error');
    const fieldWarnings = fieldIssues.filter((i) => i.level === 'warning');
    if (fieldErrors.length) {
      items.push({
        id: 'field-errors',
        level: 'blocker',
        message: `${fieldErrors.length} field(s) need fixing. First: ${fieldErrors[0].where}: ${fieldErrors[0].message}`,
      });
    }
    if (fieldWarnings.length) {
      items.push({
        id: 'field-warnings',
        level: 'warning',
        message: `${fieldWarnings.length} field(s) worth a look. First: ${fieldWarnings[0].where}: ${fieldWarnings[0].message}`,
      });
    }

    if (slugChangedAfterPublish) {
      items.push({
        id: 'slug-changed',
        level: 'warning',
        message: `Slug changed from "${publishedSlug.current}". The old URL has traffic and will 404.`,
      });
    }
    if (excerpt.length > EXCERPT_LIMIT) {
      items.push({
        id: 'excerpt-length',
        level: 'warning',
        message: `Excerpt is ${excerpt.length} characters; the public card truncates around ${EXCERPT_LIMIT}.`,
      });
    }
    // Length, internal links, placeholders, unlinked titles and the banner: worth fixing, never a
    // reason to refuse a publish.
    items.push(
      ...bodyWarnings({ contentHtml, contentJson, jsonWords: stats.words, guideTitles, slug: publishedSlug.current || slug }),
    );
    return items;
  }, [parsed, title, slug, excerpt, datePublished, slugCheck.taken, missing, stats.words, slugChangedAfterPublish, addMissingSection, hasHtmlBody, contentHtml, contentJson, fieldIssues, guideTitles]);

  /* ── Save ─────────────────────────────────────────────────────────────── */

  const persist = useCallback(
    async (nextStatus: string) => {
      const fd = new FormData();
      fd.append('slug', slug.trim());
      fd.append('pageType', pageType);
      fd.append('title', title.trim());
      fd.append('excerpt', excerpt ?? '');
      fd.append('datePublished', datePublished ?? '');
      fd.append('contentJson', contentJson);
      // Always sent, including empty: an operator who cleared the HTML is asking for the template
      // back, and a field omitted on the way out means "leave it alone" at the other end.
      fd.append('contentHtml', contentHtml);
      fd.append('status', nextStatus);
      await axios.put(`/api/guides/${guideId}`, fd);
      setDirty(false);
    },
    [guideId, slug, pageType, title, excerpt, datePublished, contentJson, contentHtml],
  );

  const { state: saveState, savedAt } = useAutosave({
    dirty,
    // Drafts only — this guide's output is public, and autosaving a published page would push
    // half-finished edits live as they are typed. Everything else saves on the explicit button
    // below; what must NOT happen is the third option this used to take, where a published guide
    // had neither and every edit was discarded on navigate without a word.
    enabled: status === 'DRAFT' && parsed.ok && !!title.trim() && !!slug.trim(),
    onSave: () => persist('DRAFT'),
  });

  const autosaves = status === 'DRAFT';
  const [savingNow, setSavingNow] = useState(false);

  const saveNow = async () => {
    setSavingNow(true);
    try {
      await persist(status);
      showToast(status === 'ACTIVE' ? 'Saved and live' : 'Saved', 'success');
    } catch (e: any) {
      // The server explains itself — a publish gate refusal names the rules that failed, and
      // replacing that with "save failed" is what sent operators hunting in the wrong place.
      showToast(e?.response?.data?.error || 'Could not save the guide', 'error');
    } finally {
      setSavingNow(false);
    }
  };

  const doPublish = async () => {
    setPublishConfirm(false);
    setPublishing(true);
    try {
      const next = status === 'ACTIVE' ? 'ARCHIVED' : 'ACTIVE';
      await persist(next);
      setStatus(next);
      publishedSlug.current = next === 'ACTIVE' ? slug.trim() : '';
      showToast(next === 'ACTIVE' ? 'Guide published' : 'Guide archived', 'success');
    } catch (e: any) {
      showToast(e?.response?.data?.error || 'Failed to save the guide', 'error');
    } finally {
      setPublishing(false);
    }
  };

  /* ── Preview / source ─────────────────────────────────────────────────── */

  // Opens the REAL public page. An approximation inside the CC drifts from the
  // public template, and authors stop trusting it.
  /*
   * In a new tab, not an iframe panel, because the site refuses to be framed: mintly-ui-new's
   * next.config.mjs sends `X-Frame-Options: SAMEORIGIN` on every route (and `frame-ancestors 'self'`
   * in a CSP that is still Report-Only). The console is another origin, so a framed preview would be
   * the browser's refusal page, and a cross-origin frame cannot even be asked whether it loaded. An
   * in-console panel needs the site to drop X-Frame-Options on `?preview=` requests and send an
   * enforced `frame-ancestors 'self' <control-center origin>` there instead.
   */
  const openPreview = async () => {
    try {
      /*
       * The preview reads the STORED record, so unsaved work is not in it.
       *
       * This used to be `if (dirty) await persist(status)`, which on a published guide pushed the
       * unsaved edits straight to the public site — previewing is a request to look, not to
       * publish. Saving them as a draft instead would be worse still: that unpublishes the page.
       * So a published guide is never written from here; the operator is told what they are about
       * to see and can press Save first. A draft saves silently, because its status does not move
       * and nothing is public.
       */
      if (dirty) {
        if (status === 'DRAFT') {
          await persist('DRAFT');
        } else {
          showToast('Showing the saved version. Press Save first to preview your changes.', 'info');
        }
      }
      const { data } = await axios.get(`/api/guides/${guideId}/preview-url`);
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (e: any) {
      /*
       * The server explains itself precisely — a 503 names the missing MAIN_SITE_URL or
       * GUIDE_PREVIEW_SECRET, and a publish refusal names the rules that failed. Replacing that
       * with "Failed to open the preview" is what left an unconfigured environment looking like a
       * broken button.
       */
      showToast(e?.response?.data?.error || 'Failed to open the preview', 'error');
    }
  };

  const downloadSource = async () => {
    try {
      const { data } = await axios.get(`/api/guides/${guideId}/source`);
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch {
      showToast('No source file available', 'error');
    }
  };

  /**
   * Re-import replaces contentJson from a re-uploaded document, reusing the
   * same extraction route the guide was originally built with, so the output
   * shape is identical. Inspector fields are left alone — the author owns
   * those, and a re-import should not silently rewrite a hand-edited excerpt.
   */
  const handleReimport = async (fileList: FileList | null) => {
    const file = fileList?.[0];
    if (!file) return;
    setReimporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('pageType', pageType);
      /*
       * This guide owns its row. Without saying so, /extract made an IMPORTING stub of its own, and
       * the stub then tried to take the model's slug, which is this guide's slug: the backend
       * refuses that, and the stub sat at IMPORTING in the list forever.
       */
      fd.append('reimportFor', guideId);
      const res = await fetch('/api/guides/extract', { method: 'POST', body: fd });
      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => null);
        throw new Error(err?.error || 'Extraction failed');
      }
      // The route streams newline-delimited stage events and ends with the guide.
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let result: any = null;
      let doneEvent: Record<string, any> | null = null;
      let streamError = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.trim()) continue;
          const evt = JSON.parse(line);
          if (evt.stage === 'done') {
            result = evt.guide;
            doneEvent = evt;
          } else if (evt.stage === 'error') streamError = evt.error;
        }
      }
      if (streamError) throw new Error(streamError);
      if (!result?.contentJson) throw new Error('Re-import returned no content');

      setContentJson(JSON.stringify(JSON.parse(result.contentJson), null, 2));
      /*
       * The generator is structured now, so a re-import moves an HTML guide onto the template: the
       * HTML body is cleared (saved as an empty string, which is what makes the site draw the
       * template) and the typed editor opens on the new contentJson. That is the intended way
       * across: re-run the document and the guide arrives on the new path.
       */
      if (result.format === 'structured') setContentHtml('');
      else if (typeof result.contentHtml === 'string') setContentHtml(result.contentHtml);
      const report = reportFromDone(doneEvent);
      setReimportReport(report);
      setDirty(true);
      const cost = report?.usage ? ` (${formatCost(report.usage.estimatedCostUsd)})` : '';
      showToast(`Body replaced from the re-imported document${cost}. Review before publishing.`, 'success');
    } catch (e: any) {
      showToast(e?.message || 'Re-import failed', 'error');
    } finally {
      setReimporting(false);
      if (reimportInput.current) reimportInput.current.value = '';
    }
  };

  /**
   * Deleting is kept out of the header, which carries only Preview and the one
   * state change. It lives in the inspector, behind a confirm that names what
   * is lost. Note archiving is NOT a way to keep the URL alive: the public query is
   * `findBySlugAndStatus(slug, ACTIVE)`, so an archived guide 404s exactly as a deleted one does.
   * What archiving keeps is the record, and the option to publish it again.
   */
  const handleDelete = async () => {
    setConfirmDelete(false);
    setDeleting(true);
    try {
      await axios.delete(`/api/guides/${guideId}`);
      showToast('Guide deleted', 'success');
      navigate('/guides');
    } catch {
      showToast('Failed to delete the guide', 'error');
      setDeleting(false);
    }
  };

  if (loading) return <SkeletonLoader />;

  const statusTone =
    status === 'ACTIVE'
      ? { tone: 'var(--green)', tint: 'var(--green-tint)' }
      : status === 'ARCHIVED'
        ? { tone: 'var(--grey)', tint: 'var(--grey-tint)' }
        : { tone: 'var(--amber)', tint: 'var(--amber-tint)' };

  /* ── Inspector ────────────────────────────────────────────────────────── */

  const excerptOver = excerpt.length > EXCERPT_LIMIT;

  /* Groups run from what the guide IS to what can be done to it: Identity, Classification, Hero
     image, Source, Reach, and Danger zone last, so the destructive action is the furthest thing
     from the fields an author edits every visit. */
  const inspector = (
    <>
      <InspectorGroup title="Identity" icon={<BadgeOutlinedIcon />}>
        {/* Multiline so a long title reads in full instead of being cut off at the inspector's
            width. Enter is swallowed: a title is one line on the site, whatever wraps here. */}
        <FormField
          required
          label="Title"
          name="title"
          multiline
          value={title}
          textFieldProps={{ rows: undefined, minRows: 1, maxRows: 4, onKeyDown: blockEnter }}
          onChange={(_, v) => {
            const next = String(v ?? '').replace(/\n/g, ' ');
            setTitle(next);
            // Only auto-derive while the slug has never been published.
            if (!publishedSlug.current && (!slug || slug === deriveSlug(title))) {
              setSlug(deriveSlug(next));
            }
            markDirty();
          }}
        />
        <FormField
          required
          label="Slug"
          name="slug"
          value={slug}
          onChange={(_, v) => { setSlug(String(v ?? '')); markDirty(); }}
          helperText={
            publishedSlug.current
              ? 'Published. Changing this breaks every live link to the guide.'
              : 'Derived from the title until you edit it.'
          }
        />
        {/* Grows with its text, so nothing is clipped mid-line, and the counter turns amber past
            the length the public card shows: the author sees the overrun where they are typing,
            not only in the validation bar. */}
        <FormField
          required={false}
          label="Excerpt"
          name="excerpt"
          multiline
          value={excerpt}
          onChange={(_, v) => { setExcerpt(String(v ?? '')); markDirty(); }}
          helperText={
            excerptOver
              ? `${excerpt.length}/${EXCERPT_LIMIT}: the public card cuts off the last ${excerpt.length - EXCERPT_LIMIT} characters.`
              : `${excerpt.length}/${EXCERPT_LIMIT} before the public card truncates.`
          }
          textFieldProps={{
            rows: undefined,
            minRows: 3,
            maxRows: 12,
            slotProps: { formHelperText: { className: excerptOver ? 'ge-helper--over' : undefined } },
          }}
        />
      </InspectorGroup>

      <InspectorGroup title="Classification" icon={<LabelOutlinedIcon />}>
        <SelectFormField
          required
          label="Guide type"
          name="pageType"
          value={pageType}
          options={PAGE_TYPE_OPTIONS}
          onChange={(_, v) => { setPageType(String(v)); markDirty(); }}
          helperText="Decides which sections the public page requires."
        />
        <SelectFormField
          required={false}
          label="Status"
          name="status"
          value={status}
          options={STATUS_OPTIONS}
          onChange={(_, v) => { setStatus(String(v)); markDirty(); }}
        />
        <FormField
          required={false}
          label="Date published"
          name="datePublished"
          value={datePublished}
          onChange={(_, v) => { setDatePublished(String(v ?? '')); markDirty(); }}
          textFieldProps={{ type: 'date' }}
          helperText="Back-dating migrated content is legitimate."
        />
      </InspectorGroup>

      <InspectorGroup title="Hero image" icon={<ImageOutlinedIcon />}>
        <HeroImageField
          contentJson={contentJson}
          onChange={(next) => { setContentJson(next); markDirty(); }}
          slug={slug}
        />
      </InspectorGroup>

      <InspectorGroup title="Source" icon={<SourceOutlinedIcon />}>
        <p className="cc-ed__meta">
          {sourceFileUrl ? 'The document this guide was imported from is archived.' : 'No source document archived.'}
        </p>
        <Box className="cc-ed__group-actions">
          <Button
            variant="outlined"
            startIcon={<UploadFileOutlinedIcon />}
            disabled={reimporting}
            onClick={() => reimportInput.current?.click()}
          >
            {reimporting ? 'Re-importing…' : 'Re-import document'}
          </Button>
          {sourceFileUrl && (
            <ActionIconButton label="Download source" icon={<FileDownloadOutlinedIcon />} onClick={downloadSource} />
          )}
        </Box>
        <input
          ref={reimportInput}
          type="file"
          accept=".docx,.pdf,.txt"
          hidden
          onChange={(e) => handleReimport(e.target.files)}
        />
        <p className="cc-ed__hint">Replaces the body only. Title, slug and excerpt are left as you wrote them.</p>
      </InspectorGroup>

      <InspectorGroup title="Reach" icon={<InsightsOutlinedIcon />}>
        <p className="cc-ed__meta">
          {viewCount === null ? 'Views not recorded yet.' : `${viewCount.toLocaleString()} views`}
        </p>
        <p className="cc-ed__meta">
          {stats.words.toLocaleString()} words · {stats.minutes} min read
        </p>
      </InspectorGroup>

      <InspectorGroup title="Danger zone" icon={<DeleteOutlineIcon />} tone="danger">
        <Box className="cc-ed__group-actions">
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            disabled={deleting}
            onClick={() => setConfirmDelete(true)}
          >
            {deleting ? 'Deleting…' : 'Delete guide'}
          </Button>
        </Box>
        <p className="cc-ed__hint">
          {publishedSlug.current
            ? 'Published. Archiving keeps the record, but the URL 404s either way.'
            : 'Permanent. There is no undo.'}
        </p>
      </InspectorGroup>
    </>
  );

  /* ── Canvas ───────────────────────────────────────────────────────────── */

  const destination = publicPath(pageType, slug.trim());

  return (
    <>
      <EditorShell
        backLabel="Guides"
        onBack={() => navigate('/guides')}
        name={title || 'Untitled guide'}
        status={STATUS_LABELS[status] ?? status}
        statusTone={statusTone.tone}
        statusTint={statusTone.tint}
        saveState={savingNow ? 'saving' : saveState}
        savedAt={savedAt}
        unsavedLabel={!autosaves && dirty ? 'unsaved changes' : undefined}
        previewLabel="Preview public page"
        previewIcon={<OpenInNewOutlinedIcon />}
        onPreview={openPreview}
        secondaryAction={
          autosaves
            ? undefined
            : { label: 'Save', onClick: saveNow, disabled: !dirty || !parsed.ok, busy: savingNow }
        }
        stateAction={{
          label: status === 'ACTIVE' ? 'Archive' : 'Publish',
          onClick: () => setPublishConfirm(true),
          busy: publishing,
          // Taking a page down is the opposite of publishing it, so the publish blockers must not
          // stand in its way. A guide that breaks the house rules is the one you most need to
          // archive, and it was the one this refused to archive.
          bypassBlockers: status === 'ACTIVE',
        }}
        validation={validation}
        inspector={inspector}
        redirectTitle="Guide editor"
        redirectReason="Authoring a public guide needs the full section list and the inspector side by side."
      >
        <Box className="ge-canvas">
          {/* One body, one editor. A guide with an HTML body has no sections to edit, and showing
              both would invite an operator to edit the one the page does not render. */}
          {hasHtmlBody ? (
            <HtmlBodyEditor
              title={title}
              slug={slug}
              value={contentHtml}
              onChange={(next) => {
                setContentHtml(next);
                setDirty(true);
              }}
            />
          ) : (
            <Box className="ge-doc">
              <Box className="ge-stats">
                {stats.words.toLocaleString()} words · {stats.minutes} min read
              </Box>
              {reimportReport && (
                <ImportReport report={reimportReport} onDismiss={() => setReimportReport(null)} />
              )}
              <StructuredBodyEditor
                contentJson={contentJson}
                pageType={pageType}
                slug={slug}
                onChange={editBody}
              />
            </Box>
          )}
        </Box>
      </EditorShell>

      {/* Publish names the destination, so the author knows what goes live and where. */}
      <ConfirmDialog
        open={publishConfirm}
        title={status === 'ACTIVE' ? 'Archive this guide?' : 'Publish this guide?'}
        body={
          status === 'ACTIVE' ? (
            <>
              It will stop appearing on the public site and <code>{destination}</code> will
              return 404, the same as deleting. What archiving keeps is the record, so you can put
              it back without rebuilding it.
            </>
          ) : (
            <>
              It will go live at <code>{destination}</code> as a {stats.minutes} minute read.
            </>
          )
        }
        actionLabel={status === 'ACTIVE' ? 'Archive' : 'Publish'}
        onConfirm={doPublish}
        onCancel={() => setPublishConfirm(false)}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this guide permanently?"
        body={
          publishedSlug.current ? (
            <>
              <strong>{title || 'This guide'}</strong> is published at{' '}
              <code>{publicPath(pageType, slug.trim())}</code>, so that URL starts returning 404.
              Archiving returns 404 too, but keeps the record so it can be republished.
            </>
          ) : (
            <>
              <strong>{title || 'This guide'}</strong> and its content are removed. It has never
              been published, so no public URL is affected.
            </>
          )
        }
        busy={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </>
  );
};

export default GuideEditorShell;
